/**
 * A frame's natural size, the input of focus boxes, and the
 * planner environment built from this window. Both renderers read sizes
 * here, so the same frame is sized the same everywhere, except an object,
 * which the wall opens at its card (`opensAtNodeSize`).
 */
import { frameCardFamily, type FrameCardFamily } from "@/lib/frame-card-shape";
import type { FrameData } from "@/lib/frame-data";
import { frameNodeSize } from "@/lib/frame-node-geometry";
import type {
  FocusNaturalSize,
  PresentationState,
} from "@/lib/focus/focus-types";
import {
  openedNaturalSize,
  roundNaturalSize,
} from "@/lib/focus/focus-opened-size";
import type { FocusPlanEnv } from "@/lib/focus/focus-plan-layout";
import { focusRendererKind } from "@/lib/focus/focus-renderer-kind";
import { currentFocusSurfaceBox } from "@/lib/focus/focus-surface";
import { useCanvasStore } from "@/stores/canvas-store";
import { useFramesStore } from "@/stores/frames-store";

function canvasNode(frameId: string) {
  return useCanvasStore.getState().nodes.find((node) => node.id === frameId);
}

/**
 * Whether a frame of `family` opens at its canvas node's size. A media
 * frame never does: it opens at its card's aspect. An object does only on
 * the canvas, which draws the node; above the wall it wears its card at
 * 24px (Part 10, Rule 1), which the node a detail load places (a widget's
 * 180x130) would crop to an unreadable window.
 */
function opensAtNodeSize(family: FrameCardFamily): boolean {
  if (family === "media") return false;
  return family !== "object" || focusRendererKind() === "canvas";
}

/**
 * The size a frame opens at (`openedNaturalSize`), or its canvas node's
 * (`opensAtNodeSize`). A frame known by neither card nor content is sized
 * provisionally, until its detail lands.
 */
export function frameNaturalSize(frameId: string): FocusNaturalSize {
  const frame = useFramesStore.getState().framesById[frameId];
  const family = frameCardFamily(frame?.frameType, frame);
  const node = opensAtNodeSize(family) && canvasNode(frameId);
  if (node) {
    return {
      ...frameNodeSize(node),
      container: family === "object" ? "object" : "normal",
    };
  }
  if (!frame) return { width: 400, height: 300 };
  const size = openedNaturalSize(frame, currentFocusSurfaceBox());
  return frame.contentState === "card" && !frame.card
    ? { ...size, provisional: true }
    : size;
}

/** The size a frame takes in an agent round: content-sized (plan 13). */
export function frameRoundSize(frameId: string): FocusNaturalSize {
  const frame = useFramesStore.getState().framesById[frameId];
  return frame
    ? roundNaturalSize(frame, currentFocusSurfaceBox())
    : frameNaturalSize(frameId);
}

/** Provisional sizes of frames whose detail just landed, measured again. */
export function landedNaturalSizes(
  presentation: PresentationState,
  framesById: Readonly<Record<string, FrameData>>,
  previousFramesById: Readonly<Record<string, FrameData>>,
): Record<string, FocusNaturalSize> | null {
  let landed: Record<string, FocusNaturalSize> | null = null;
  for (const frameId of presentation.frameIds) {
    const frame = framesById[frameId];
    if (
      previousFramesById[frameId]?.contentState !== "card" ||
      !frame ||
      frame.contentState === "card" ||
      !presentation.naturalSizes[frameId]?.provisional
    ) {
      continue;
    }
    landed = { ...(landed ?? {}), [frameId]: frameNaturalSize(frameId) };
  }
  return landed;
}

export function currentFocusPlanEnv(): FocusPlanEnv {
  const { framesById } = useFramesStore.getState();
  return {
    surface: currentFocusSurfaceBox(),
    naturalSize: frameNaturalSize,
    roundSize: frameRoundSize,
    frameType: (frameId) => framesById[frameId]?.frameType,
    workspaceBox: (frameId) => {
      const node = canvasNode(frameId);
      return node ? { ...node.position, ...frameNodeSize(node) } : null;
    },
  };
}
